import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Signup.css';
import "../../Styles/common-styles.css";
import "../Styles/common-formstyle.css";

// ✅ Import from the correct location
import { requestOTP, validateOTP } from "../../../api/auth/otpService";
import kleidoformimage from "../../../../assets/images/Kleido.png";
import { toast } from "react-toastify";
import { signupUser, validateSignupData } from '../../../api/auth/userSignup';
import { USE_MOCK_DATA } from '../../../config/apiConfig';

const Signup = () => {
  const [formData, setFormData] = useState({
    userId: '',
    email: '',
    mobile: '',
    password: '',
    userType: "user"
  });

  const [userName, setUserName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [location, setLocation] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPasswordConfirmed, setIsPasswordConfirmed] = useState(false);

  const navigate = useNavigate();
  const emailRef = useRef(null);

  const handlePasswordKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && formData.password) {
      e.preventDefault();
      setShowPopup(true);
      setConfirmPassword('');
      setError('');
    }
  };

  /**
   * ✅ Handle OTP Request using centralized service
   */
  const handleRequestOTP = async () => {
    if (formData.mobile.length !== 10) {
      setErrorMessage("Enter a valid 10-digit mobile number");
      return;
    }

    setErrorMessage("");
    const result = await requestOTP(formData.mobile);
    
    if (result.success) {
      setIsOtpSent(true);
      setShowOTPModal(true);
      toast.info("OTP sent to your mobile number");
      
      // ✅ Show OTP in console for testing (mock mode only)
      if (USE_MOCK_DATA && result.otp) {
        console.log('🎭 MOCK OTP:', result.otp);
        toast.info(`🎭 Mock OTP: ${result.otp}`, { autoClose: 5000 });
      }
    } else {
      setErrorMessage(result.message);
      toast.error(result.message);
    }
  };

  /**
   * ✅ Handle OTP Validation using centralized service
   */
  const handleValidateOTP = async () => {
    if (!otp || otp.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit OTP");
      return;
    }

    setErrorMessage("");
    const result = await validateOTP(formData.mobile, otp);
    
    if (result.success) {
      setIsOtpVerified(true);
      setShowOTPModal(false);
      toast.success("OTP Verified successfully!");
      
      // Store device ID if provided
      if (result.deviceId) {
        localStorage.setItem('deviceId', result.deviceId);
      }
    } else {
      setErrorMessage(result.message);
      toast.error(result.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (value === formData.password) {
      setIsPasswordConfirmed(true);
      setShowPopup(false);
      setError('');
      setTimeout(() => {
        emailRef.current?.focus();
      }, 0);
    } else {
      setIsPasswordConfirmed(false);
      setError('Password confirmation does not match.');
    }
  };

  const parseLocation = (locationString) => {
    if (!locationString) return { city: '', country: '' };
    
    const parts = locationString.split(',').map(part => part.trim());
    
    if (parts.length >= 2) {
      return {
        city: parts[0],
        country: parts[1]
      };
    } else {
      return {
        city: parts[0],
        country: ''
      };
    }
  };

  /**
   * ✅ Handle submit using the service layer
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    // Validation
    if (!acceptedTerms) {
      setErrorMessage('You must accept the Terms and Conditions to continue.');
      setIsLoading(false);
      return;
    }

    if (formData.password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (!isOtpVerified) {
      setErrorMessage('Please verify your mobile number with OTP');
      setIsLoading(false);
      return;
    }

    // Parse location
    const { city, country } = parseLocation(location);

    // Prepare signup data
    const signupData = {
      userId: formData.userId || "",
      name: userName,
      email: formData.email,
      dateOfBirth: dateOfBirth,
      profilePicture: "",
      placeOfBirth: "",
      password: formData.password,
      city: city,
      country: country,
      gender: "",
      nationality: "",
      mobileNo: formData.mobile,
      acceptTerms: acceptedTerms ? 1 : 0,
      userType: formData.userType || "user",
    };

    // Validate data before sending
    const validation = validateSignupData(signupData);
    if (!validation.isValid) {
      setErrorMessage(validation.errors.join(', '));
      setIsLoading(false);
      return;
    }

    console.log('📤 Submitting signup data...');

    try {
      // ✅ Call the signup service
      const response = await signupUser(signupData);

      console.log('📥 Signup response:', response);

      if (response.status === 'success') {
        // Store authentication data
        if (response.securityToken) {
          localStorage.setItem('securityToken', response.securityToken);
          localStorage.setItem('userId', response.userId);
        }

        // Show success message
        toast.success(
          response.message || "Signup successful! 🎉 You can now sign in to your account.",
          {
            position: "top-center",
            autoClose: 3000,
            onClose: () => navigate("/login"),
          }
        );

        // Clear form
        setFormData({
          userId: '',
          email: '',
          mobile: '',
          password: '',
          userType: "user"
        });
        setUserName("");
        setDateOfBirth("");
        setLocation("");
        setConfirmPassword("");
        setAcceptedTerms(false);
        setIsOtpVerified(false);
      } else {
        const errorMsg = response.message || 'Signup failed. Please try again.';
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg = error.message || 'An unexpected error occurred.';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
      console.error('❌ Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="su-auth-container">
      <div className="signup-box">
        <div className="form-box-header">
          <h2>Sign Up</h2>
          <img
            src={kleidoformimage}
            className="logo-default-container-img"
            alt="Oowlet Logo"
          />
        </div>

        {/* ✅ Mock Mode Banner */}
        {USE_MOCK_DATA && (
          <div style={{
            backgroundColor: '#fff3cd',
            color: '#856404',
            padding: '10px',
            borderRadius: '5px',
            marginBottom: '15px',
            textAlign: 'center',
            fontWeight: '600',
          }}>
            🎭 MOCK MODE: Any 6-digit OTP will work for testing
          </div>
        )}
        
        {/* {errorMessage && <p className="error-message">{errorMessage}</p>} */}
        
        <form onSubmit={handleSubmit}>
          {/* User ID */}
          <div className="form-group-row">
            <div className="form-group">
              <label>* User Id</label>
              <input
                type="text"
                name="userId"
                value={formData.userId}
                onChange={handleChange}
                style={{background: "white", color: "black"}}
                required
              />
            </div>
          </div>

          {/* Full Name */}
          <div className="form-group-row">
            <div className="form-group">
              <label>* Full Name</label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                style={{background: "white", color: "black"}}
                required
              />
            </div>
          </div>

          {/* Email */}
          <div className="form-group-row">
            <div className="form-group">
              <label>* Email ID</label>
              <input
                type="text"
                name="email"
                value={formData.email}
                onChange={handleChange}
                ref={emailRef}
                style={{background: "white", color: "black"}}
                required
              />
            </div>
          </div>

          {/* Date of Birth */}
          <div className="form-group-row">
            <div className="form-group" style={{marginTop: 5}}>
              <label>* Date Of Birth</label>
              <input
                type="date"
                name="dateOfBirth"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                required
                style={{marginTop: 10}}
              />
            </div>
          </div>

          {/* Password */}
          <div className="form-group-row">
            <div className="form-group" style={{marginTop: 10}}>
              <label>* Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                onKeyDown={handlePasswordKeyDown}
                required
                style={{height: 50, marginTop: 10}}
              />
              {showPopup && (
                <div style={popupStyle}>
                  <h3>Confirm Your Password</h3>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    autoFocus
                    style={{ padding: '0.5rem' }}
                  />
                  {error && (
                    <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="form-group-row">
            <div className="form-group" style={{marginTop: 10}}>
              <label>* Current Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Mumbai, India"
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Format: City, Country
              </small>
            </div>
          </div>

          {/* Mobile Number & OTP - ✅ FIXED SYNTAX ERROR */}
          <div className="form-group-row">
            <div className="form-group" style={{marginTop: 10}}>
              <label>* Mobile Number</label>
              <div style={{display: "flex", flexDirection: "row", gap: "10px"}}>
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  disabled={!isPasswordConfirmed}
                  maxLength={10}
                  required
                  style={{flex: 1}}
                />
                <button 
                  type="button"
                  onClick={handleRequestOTP} 
                  disabled={isOtpSent || !isPasswordConfirmed || formData.mobile.length !== 10}
                  className='submit-otp-btn'
                >
                  <span style={{fontSize: 15}}>
                    {isOtpSent ? "OTP Sent ✓" : "Get OTP"}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* OTP Modal */}
          {showOTPModal && (
            <div style={modalOverlayStyle}>
              <div style={modalContentStyle}>
                <h3>Enter OTP</h3>
                <p>We've sent a 6-digit OTP to {formData.mobile}</p>

                {USE_MOCK_DATA && (
                  <div style={{
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    padding: '10px',
                    borderRadius: '5px',
                    marginBottom: '15px',
                    textAlign: 'center',
                  }}>
                    🎭 Mock Mode: Any 6-digit number works
                  </div>
                )}

                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  style={{ 
                    padding: '0.5rem', 
                    marginBottom: '1rem',
                    width: '100%',
                    fontSize: '20px',
                    textAlign: 'center',
                    letterSpacing: '5px',
                  }}
                  autoFocus
                />

                {errorMessage && (
                  <p style={{ color: 'red', marginBottom: '1rem' }}>
                    {errorMessage}
                  </p>
                )}

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleValidateOTP}
                    disabled={otp.length !== 6}
                    style={{
                      flex: 1,
                      padding: '0.5rem 1rem',
                      background: otp.length === 6 ? '#28a745' : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: otp.length === 6 ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Verify OTP
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleRequestOTP}
                    style={{
                      flex: 1,
                      padding: '0.5rem 1rem',
                      background: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                    }}
                  >
                    Resend OTP
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowOTPModal(false);
                      setOtp("");
                      setErrorMessage("");
                    }}
                    style={{
                      flex: 1,
                      padding: '0.5rem 1rem',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Terms & Conditions */}
          <div className="form-group" style={{marginTop: 20}}>
            <input 
              type="checkbox" 
              id="terms" 
              checked={acceptedTerms} 
              onChange={() => setAcceptedTerms(!acceptedTerms)}
              disabled={!isOtpVerified}
            />
            <label htmlFor="terms" style={{marginLeft: 5}}>
              I accept the{' '}
              <Link 
                to="/privacy-policy" 
                style={{ color: "#007BFF" }} 
                target="_blank"
              >
                Terms and Conditions
              </Link>
            </label>
          </div>

          {/* Submit Button */}
          <div className="form-group-row" style={{marginTop: 10}}>
            <button 
              type="submit" 
              disabled={!acceptedTerms || !isOtpVerified || isLoading}
              className='submit-btn'
              style={{
                background: "rgb(34, 31, 31)",
                opacity: (!acceptedTerms || !isOtpVerified || isLoading) ? 0.6 : 1,
                cursor: (!acceptedTerms || !isOtpVerified || isLoading) ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Signing Up...' : 'Sign Up'}
            </button>
          </div>

          <p className="signin-prompt" style={{fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"}}>
            Already have an account? <Link to="/signin">Sign In</Link>
          </p>
          
          <div className='copyright-container' style={{marginTop: 10}}>
            <p className="cmn-copyright" style={{color: "black"}}>
              Kleido copyright To InitiumNet Technology Pvt Ltd. More information about privacy policies and cookies, click{' '}
              <Link to="/privacy-policy" style={{ color: "#007BFF" }}>here</Link>.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const popupStyle = {
  position: 'fixed',
  top: '30%',
  left: '50%',
  transform: 'translate(-50%, -30%)',
  backgroundColor: '#fff',
  padding: '2rem',
  border: '1px solid #ccc',
  borderRadius: '10px',
  boxShadow: '0 0 10px rgba(0,0,0,0.2)',
  zIndex: 1000,
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
};

const modalContentStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  maxWidth: '400px',
  width: '90%',
};

export default Signup;
